import React, { useState } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Eye, Pencil, Search } from 'lucide-react';
import { ClientDetailModal } from './ClientDetailModal';

export const ClientsTab = ({ clients, loadData, activities = [] }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [search, setSearch] = useState('');

  const openNew = () => { setEditing(null); setForm({ name: '', email: '', phone: '' }); setShowForm(true); };
  const openEdit = (c, e) => { e.stopPropagation(); setEditing(c); setForm({ name: c.name, email: c.email, phone: c.phone || '' }); setShowForm(true); };
  const save = async () => {
    if (!form.name || !form.email) { toast.error('Nom et email requis'); return; }
    try {
      if (editing) { await adminAPI.updateClient(editing.id, form); toast.success('Client mis à jour'); }
      else { await adminAPI.createClient(form); toast.success('Client créé'); }
      setShowForm(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const filtered = clients.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search)
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h3 className="font-heading text-lg font-bold text-white">CRM Clients ({clients.length})</h3>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="pl-9 bg-[#0A0A0A] border-[#2A2A2A] text-white w-full sm:w-56"
              data-testid="client-search-input"
            />
          </div>
          <Button onClick={openNew} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="create-client-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Nouveau
          </Button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-clients-table">
            <thead className="bg-[#111] border-b border-[#2A2A2A]">
              <tr>
                {['Nom', 'Email', 'Téléphone', 'Réservations', 'Dernière résa', 'Actions'].map(h =>
                  <th key={h} className="text-left p-4 font-semibold text-slate-400">{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-[#1A1A1A] hover:bg-[#222] cursor-pointer transition-colors"
                  onClick={() => setSelectedClientId(c.id)}
                  data-testid={`client-${c.id}`}
                >
                  <td className="p-4 font-medium text-white">{c.name}</td>
                  <td className="p-4 text-slate-400">{c.email}</td>
                  <td className="p-4 text-slate-400">{c.phone || '-'}</td>
                  <td className="p-4">
                    <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0">{c.booking_count}</Badge>
                  </td>
                  <td className="p-4 text-slate-500 text-xs">{c.last_booking_date || '-'}</td>
                  <td className="p-4 flex gap-2">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedClientId(c.id); }} className="text-blue-400 border-blue-500/30" data-testid={`view-client-${c.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                    </Button>
                    <Button variant="outline" size="sm" onClick={(e) => openEdit(c, e)} className="text-[#00FF00] border-[#00FF00]/30" data-testid={`edit-client-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-600">Aucun client trouvé</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Detail Modal (rich) */}
      <ClientDetailModal
        clientId={selectedClientId}
        open={!!selectedClientId}
        onClose={() => setSelectedClientId(null)}
        onDeleted={() => { setSelectedClientId(null); loadData(); }}
        activities={activities}
      />

      {/* Client Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md bg-[#1A1A1A] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editing ? 'Modifier' : 'Nouveau client'}</DialogTitle>
            <DialogDescription className="text-slate-500">{editing?.email || 'Créer un client'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-300">Nom *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="client-name" />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Email *</Label>
              <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="client-email" />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Téléphone</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="client-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={save} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="save-client-btn">{editing ? 'Enregistrer' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
